/**
 * Surroundings view: a small 7×7 glyph render centered on the player, drawn
 * north-up next to the minimap. While focused, the main camera locks onto the
 * enemy (pushing the player off-centre), so this keeps the player's immediate
 * surroundings — nearby tiles and guards — always visible and centered.
 *
 * Canvas + 2D fillText (the established UI-panel pattern; the CLAUDE.md
 * no-fillText rule applies to the game world, not the HUD panels).
 */

import { CONFIG } from '../config';
import type { FovMemory } from '../sim/fovMemory';
import type { Guard } from '../sim/guard';
import { isUnlit } from '../sim/light';
import { glyphAt, tileAt, type MapData } from '../sim/map';
import type { Player } from '../sim/player';
import { defaultFg } from '../sim/tiles';
import type { GameWindow } from './window';

/** Half-extent: 3 → a 7×7 window (player at the centre cell). */
const RADIUS = 3;
const SPAN = RADIUS * 2 + 1;
const CELL = 18;
/** "6th sense" tint for tiles/entities you sense in the dark up close. */
const SENSE_PURPLE = '#a066ff';

function toCss(rgb: number): string {
  return '#' + (rgb & 0xffffff).toString(16).padStart(6, '0');
}

export class SurroundView {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  constructor(win: GameWindow) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = SPAN * CELL;
    this.canvas.height = SPAN * CELL;
    this.canvas.className = 'surround-canvas';
    this.canvas.style.imageRendering = 'pixelated';
    win.body.appendChild(this.canvas);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('surroundView: no 2d context');
    this.ctx = ctx;
    ctx.font = `${CELL - 2}px "Cascadia Mono", "Consolas", "DejaVu Sans Mono", monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
  }

  update(map: MapData, fov: FovMemory, player: Player, guards: readonly Guard[]): void {
    const c = this.ctx;
    const W = this.canvas.width;
    const H = this.canvas.height;
    c.fillStyle = '#07070b';
    c.fillRect(0, 0, W, H);

    const px = player.x;
    const py = player.y;
    // Cell (dx, dy) relative to the player → canvas pixel centre (north-up).
    const cxOf = (dx: number) => (dx + RADIUS) * CELL + CELL / 2;
    const cyOf = (dy: number) => (dy + RADIUS) * CELL + CELL / 2;

    // Tiles. Dark tiles read as a close-range "6th sense": only the ones within
    // `proximityTiles` of the player render at all, tinted purple; darkness
    // further out is simply unseen. Lit tiles render normally.
    const prox = CONFIG.vision.proximityTiles;
    for (let dy = -RADIUS; dy <= RADIUS; dy++) {
      for (let dx = -RADIUS; dx <= RADIUS; dx++) {
        const x = px + dx;
        const y = py + dy;
        if (x < 0 || y < 0 || x >= map.width || y >= map.height) continue;
        const idx = y * map.width + x;
        if (!fov.isSeen(idx)) continue;
        const t = tileAt(map, x, y);
        const glyph = glyphAt(map, x, y);
        if (glyph === ' ') continue;
        const dark = isUnlit(map, x, y);
        if (dark) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) > prox) continue; // dark & far → unseen
          c.globalAlpha = 1;
          c.fillStyle = SENSE_PURPLE; // 6th-sense glow up close
        } else {
          c.globalAlpha = fov.isCurrentlyVisible(idx) ? 1 : 0.35;
          c.fillStyle = toCss(defaultFg(t));
        }
        c.fillText(glyph, cxOf(dx), cyOf(dy));
      }
    }
    c.globalAlpha = 1;

    // Guards inside the window (honour FOV / marked / ghost like the minimap).
    // A guard within the 6th-sense radius shows at its real position even in the
    // dark / out of FOV, tinted purple.
    for (const g of guards) {
      const cheb = Math.max(Math.abs(g.x - px), Math.abs(g.y - py));
      const proximity = cheb <= prox && !g.body.dead;
      if (!proximity && !g.everSeenByPlayer) continue;
      const idx = g.y * map.width + g.x;
      const inFov = fov.isCurrentlyVisible(idx);
      const useReal = inFov || g.marked || proximity;
      const gx = useReal ? g.x : g.lastSeenX;
      const gy = useReal ? g.y : g.lastSeenY;
      const dx = gx - px;
      const dy = gy - py;
      if (Math.abs(dx) > RADIUS || Math.abs(dy) > RADIUS) continue;
      const sensedInDark = proximity && isUnlit(map, g.x, g.y);
      c.globalAlpha = inFov || proximity ? 1 : 0.5;
      c.fillStyle = g.body.dead ? '#8a4a4a' : sensedInDark ? SENSE_PURPLE : inFov ? '#ff3b52' : '#a05050';
      c.fillText(g.body.dead ? '%' : 'g', cxOf(dx), cyOf(dy));
      // Highlight the focused target with a box.
      if (!g.body.dead && player.focusedTargetId === g.id) {
        c.globalAlpha = 1;
        c.strokeStyle = '#ffe060';
        c.lineWidth = 1.5;
        c.strokeRect((dx + RADIUS) * CELL + 1.5, (dy + RADIUS) * CELL + 1.5, CELL - 3, CELL - 3);
      }
    }
    c.globalAlpha = 1;

    // Player at the centre.
    c.fillStyle = '#22e6ff';
    c.fillText('@', cxOf(0), cyOf(0));
  }
}
